import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";

export default function Home() {
  const { t } = useTranslation("");
  const navigate = useNavigate();

  return (
    <>
      <div>
        {/* Start Page Hero */}
        <section className="page-hero d-flex align-items-center" id="page-hero">
          <div className="overlay-video-bg">
            <video autoPlay muted loop playsInline>
              <source src="/assets/videos/hero-bg.mp4" type="video/mp4" />
            </video>
          </div>

          <div className="particles-js bubels" id="particles-js" />
          <div className="container">
            <div className="row align-items-center">
              {/* Hero Text Area */}
              <div className="col-12 col-lg-12 d-flex align-items-center justify-content-center text-center">
                <div className="hero-text-area">
                  <div className="row">
                    <div className="col-12">
                      <div className="hero-social-icons mb-3">
                        <div className="sc-wrapper dir-row sc-flat">
                          <ul className="sc-list">
                            <li className="sc-item" title="Facebook">
                              <a
                                className="sc-link "
                                href="https://www.facebook.com/IncubationMasters/"
                                title="social media icon"
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="fab fa-facebook-f sc-icon" />
                              </a>
                            </li>
                            <li className="sc-item" title="LinkedIn">
                              <a
                                className="sc-link"
                                href="https://www.linkedin.com/company/incubationmasters/posts/?feedView=all"
                                title="social media icon"
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="fab fa-linkedin sc-icon" />
                              </a>
                            </li>
                            <li className="sc-item" title="Instagram">
                              <a
                                className="sc-link"
                                href="https://www.instagram.com/incubationmasters/?hl=en"
                                title="social media icon"
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="fab fa-instagram sc-icon" />
                              </a>
                            </li>
                            <li className="sc-item" title="X">
                              <a
                                className="sc-link"
                                href="https://x.com/IncubationMS"
                                title="social media icon"
                                target="_blank"
                                rel="noreferrer"
                              >
                                <i className="fab fa-x-twitter sc-icon" />
                              </a>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 text-center">
                      <h1 className="hero-title">
                        Turning Global Economic Complexity{" "}
                        <span className="lowercase">into</span>{" "}
                        <span className="featured-text">
                          Strategic Certainty
                        </span>
                        <span className="design-element rounded-shape stripes" />
                      </h1>
                    </div>

                    <div className="col-12 col-lg-8 mx-auto text-center">
                      <p className="hero-subtitle">
                        We support governments in attracting investment,
                        expanding trade, and strengthening innovation ecosystems
                        while enabling businesses to navigate regulatory
                        environments and access new markets.
                      </p>
                    </div>

                    <div className="col-12 g-3 ">
                      <div className="cta-links-area">
                        <Link
                          to="/contact-us"
                          className="btn-outline cat-link cat-link-outline"
                          style={{ marginRight: "15px" }}
                        >
                          Work With Us
                        </Link>
                        <button
                          onClick={() => navigate("/leadership")}
                          className="btn-outline cat-link cat-link-outline"
                        >
                          Meet Our Leaders
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* End Page Hero */}

        {/* Start Stats Section */}
        <section className="stats-bar mega-section">
          <div className="container">
            <div className="stats-grid">
              <div className="stat-item">
                <h3 className="stat-value">$2B+</h3>
                <p className="stat-label">Investment Facilitated</p>
              </div>
              <div className="stat-item">
                <h3 className="stat-value">15+</h3>
                <p className="stat-label">Years Experience</p>
              </div>
              <div className="stat-item">
                <h3 className="stat-value">14+</h3>
                <p className="stat-label">Government Clients</p>
              </div>
              <div className="stat-item">
                <h3 className="stat-value">3</h3>
                <p className="stat-label">Continents</p>
              </div>
            </div>
          </div>
        </section>
        {/* End Stats Section */}

        {/* Start Services Section */}
        <section className="services services-boxed mega-section" id="services">
          <div className="container">
            <div className="sec-heading">
              <div className="content-area">
                <span className="pre-title wow fadeInUp" data-wow-delay=".2s">
                  Our Expertise
                </span>
                <h2 className="title wow fadeInUp" data-wow-delay=".4s">
                  <span className="hollow-text">
                    Three Pillars of Economic Development
                  </span>
                </h2>
              </div>
            </div>

            <div className="row gx-4 gy-4 services-row">
              {/* Service 1: Trade Promotion */}
              <div className="col-12 col-md-6 col-lg-4 d-flex">
                <div
                  className="box service-box flex-fill wow fadeInUp reveal-start"
                  data-wow-offset={0}
                  data-wow-delay=".1s"
                >
                  <div className="service-icon">
                    <i className="flaticon-web-development font-icon" />
                  </div>
                  <span className="service-num hollow-text">1</span>
                  <div className="service-content">
                    <h3 className="service-title">Trade Promotion</h3>
                    <p className="service-label">Powered by IMTrade.ai</p>
                    <p className="service-text">
                      We help Indian states reach global markets and foreign
                      governments access Indian buyers. From export strategy to
                      buyer-seller matchmaking — we build trade corridors that
                      deliver results.
                    </p>
                  </div>
                  <a
                    href="#"
                    onClick={() => navigate("/startup-consulting")}
                    className="read-more"
                  >
                    read more <i className="bi bi-arrow-right icon" />
                  </a>
                </div>
              </div>

              {/* Service 2: Investment Promotion */}
              <div className="col-12 col-md-6 col-lg-4 d-flex">
                <div
                  className="box service-box flex-fill wow fadeInUp reveal-start"
                  data-wow-offset={0}
                  data-wow-delay=".2s"
                >
                  <div className="service-icon">
                    <i className="flaticon-nanotechnology font-icon" />
                  </div>
                  <span className="service-num hollow-text">2</span>
                  <div className="service-content">
                    <h3 className="service-title">Investment Promotion</h3>
                    <p className="service-label">Powered by IMFDI.ai</p>
                    <p className="service-text">
                      We help Indian states attract global investors and support
                      foreign governments in promoting their destinations to
                      Indian businesses. We also assist multinational
                      corporations in navigating India’s complex regulatory
                      landscape.
                    </p>
                  </div>
                  <a
                    href="#"
                    onClick={() => navigate("/startup-investment")}
                    className="read-more"
                  >
                    read more <i className="bi bi-arrow-right icon" />
                  </a>
                </div>
              </div>

              {/* Service 3: Innovation & Incubation */}
              <div className="col-12 col-md-6 col-lg-4 d-flex">
                <div
                  className="box service-box flex-fill wow fadeInUp reveal-start"
                  data-wow-offset={0}
                  data-wow-delay=".3s"
                >
                  <div className="service-icon">
                    <i className="flaticon-web-domain font-icon" />
                  </div>
                  <span className="service-num hollow-text">3</span>
                  <div className="service-content">
                    <h3 className="service-title">Innovation & Incubation</h3>
                    <p className="service-label">Powered by IMUnicorn.ai</p>
                    <p className="service-text">
                      We design and operate incubation centers, startup
                      programs, and innovation ecosystems for governments,
                      universities, and smart city initiatives to foster
                      entrepreneurship and innovation.
                    </p>
                  </div>
                  <a
                    href="#"
                    onClick={() => navigate("/startup-advisory")}
                    className="read-more"
                  >
                    read more <i className="bi bi-arrow-right icon" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Start Clients Section */}
        <section className="clients-section mega-section">
          <div className="container">
            <div className="sec-heading text-center justify-content-center">
              <div className="content-area flex flex-column">
                <span className="pre-title">Trusted By</span>
                <h2 className="title">Governments & Global Institutions</h2>
                <p className="subtitle">
                  We work with forward-thinking governments, global
                  institutions, and industry leaders across continents.
                </p>
              </div>
            </div>

            <div className="clients-marquee">
              <motion.div
                className="clients-track"
                animate={{ x: ["0%", "-50%"] }}
                transition={{
                  repeat: Infinity,
                  duration: 25,
                  ease: "linear",
                }}
              >
                {[
                  "/assets/assets/client-logo/logo1.png",
                  "/assets/assets/client-logo/logo2.png",
                  "/assets/assets/client-logo/logo3.png",
                  "/assets/assets/client-logo/logo4.png",
                  "/assets/assets/client-logo/logo5.png",
                  "/assets/assets/client-logo/logo6.png",
                  "/assets/assets/client-logo/logo7.png",
                  "/assets/assets/client-logo/logo8.png",
                  "/assets/assets/client-logo/logo1.png",
                  "/assets/assets/client-logo/logo2.png",
                  "/assets/assets/client-logo/logo3.png",
                  "/assets/assets/client-logo/logo4.png",
                  "/assets/assets/client-logo/logo5.png",
                  "/assets/assets/client-logo/logo6.png",
                  "/assets/assets/client-logo/logo7.png",
                  "/assets/assets/client-logo/logo8.png",
                ].map((logo, index) => (
                  <div className="client-logo" key={index}>
                    <img
                      src={logo}
                      loading="lazy"
                      alt="Client Logo"
                      draggable="false"
                    />
                  </div>
                ))}
              </motion.div>
            </div>
          </div>
        </section>

        {/* Start Differentiator Section */}
        <section className="differentiator mega-section">
          <div className="container">
            <div className="sec-heading text-center justify-content-center">
              <div className="content-area">
                <h2 className="title">We Serve Both Sides of the Table</h2>
                <p className="subtitle">
                  Most firms work with either foreign governments or Indian
                  states. By partnering with both, we bring unique insight into
                  cross-border economic development.
                </p>
              </div>
            </div>

            <div className="row gx-4 gy-4 differentiator-row">
              {/* For Indian States */}
              <div className="col-12 col-md-6 col-lg-4">
                <div className="differentiator-card">
                  <div className="card-icon">
                    <i className="flaticon-profile font-icon" />
                  </div>
                  <h3 className="card-title">For Indian States</h3>
                  <ul className="card-list">
                    <li>Export strategies</li>
                    <li>FDI attraction</li>
                    <li>Innovation ecosystems</li>
                  </ul>
                </div>
              </div>

              {/* For Foreign Governments */}
              <div className="col-12 col-md-6 col-lg-4">
                <div className="differentiator-card">
                  <div className="card-icon">
                    <i className="flaticon-search font-icon" />
                  </div>
                  <h3 className="card-title">For Foreign Governments</h3>
                  <ul className="card-list">
                    <li>India market entry</li>
                    <li>Trade missions</li>
                    <li>Buyer connections</li>
                  </ul>
                </div>
              </div>

              {/* For MNCs & Investors */}
              <div className="col-12 col-md-6 col-lg-4">
                <div className="differentiator-card">
                  <div className="card-icon">
                    <i className="flaticon-strategy font-icon" />
                  </div>
                  <h3 className="card-title">For MNCs & Investors</h3>
                  <ul className="card-list">
                    <li>Regulatory navigation</li>
                    <li>Site selection</li>
                    <li>State liaison</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>
        {/* End Differentiator Section */}
      </div>

      <style>
        {`
        /* Clients Section */
        .clients-section {
          background: #0a0f2b;
          padding: 80px 0;
          border-top: 1px solid rgba(255, 255, 255, 0.05);
        }

        .clients-section .pre-title {
          color: #1ec8ff;
          font-weight: 600;
          margin-bottom: 10px;
          display: inline-block;
        }

        .clients-marquee {
          overflow: hidden;
          position: relative;
          margin-top: 60px;
        }

        .clients-track {
          display: flex;
          gap: 60px;
          width: max-content;
        }

        .client-logo {
          min-width: 150px;
          height: 130px;
          display: flex;
          align-items: center;
          justify-content: center;
          // opacity: 0.85;
          transition: transform 0.3s ease, opacity 0.3s ease;
        }

        .client-logo img {
          max-height: 100%;
          max-width: 100%;
          object-fit: contain;

          // filter: grayscale(100%) brightness(0.9);
        }

        .client-logo:hover {
          opacity: 1;
          transform: scale(1.05);
        }

        .client-logo:hover img {
          filter: grayscale(0%) brightness(1);
        }

          .service-box {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            background: #0a0f2b;
            border: 1px solid rgba(255, 255, 255, 0.1);
            border-radius: 12px;
            padding: 30px;
            color: #fff;
            height: 100%;
            transition: transform 0.3s ease, box-shadow 0.3s ease;
          }
          .service-box:hover {
            transform: translateY(-5px);
            box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
          }
          .service-icon {
            font-size: 40px;
            margin-bottom: 15px;
            color: #1ec8ff;
          }
          .service-num {
            font-weight: 700;
            font-size: 30px;
            color: #666;
            margin-bottom: 15px;
            opacity: 0.3;
          }
          .service-label {
            font-size: 0.85rem;
            color: #1ec8ff;
            font-weight: 600;
            margin-bottom: 10px;
          }
          .service-content {
            flex-grow: 1;
          }
          .service-title {
            font-size: 1.5rem;
            margin-bottom: 10px;
            font-weight: 600;
          }
          .service-text {
            font-size: 1rem;
            line-height: 1.4;
            margin-bottom: 20px;
          }
          .read-more {
            margin-top: auto;
            font-weight: 600;
            color: #1ec8ff;
            text-decoration: none;
            display: inline-flex;
            align-items: center;
            gap: 6px;
            transition: color 0.3s ease;
            cursor: pointer;
          }
          .read-more:hover {
            color: #0bb4ff;
            text-decoration: underline;
          }
          .icon {
            font-size: 1.2rem;
          }

          /* Stats Bar Styles */
          .stats-bar {
            background: linear-gradient(135deg, #0a0f2b 0%, #1a1f3a 100%);
            border-top: 1px solid rgba(255, 255, 255, 0.1);
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            padding: 60px 0;
          }
          .stats-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 40px;
            text-align: center;
          }
          .stat-item {
            color: #fff;
          }
          .stat-value {
            font-size: 2.5rem;
            font-weight: 700;
            color: #1ec8ff;
            margin-bottom: 10px;
          }
          .stat-label {
            font-size: 1rem;
            color: #aaa;
            margin: 0;
          }

          /* Differentiator Section */
          .differentiator {
            background: #0a0f2b;
            padding: 80px 0;
          }
          .sec-heading.text-center {
            text-align: center;
            margin-bottom: 60px;
          }
          .sec-heading .title {
            font-size: 2.5rem;
            color: #fff;
            margin-bottom: 20px;
          }
          .sec-heading .subtitle {
            font-size: 1.1rem;
            color: #aaa;
            max-width: 600px;
            margin: 0 auto;
          }

          .differentiator-card {
            background: linear-gradient(135deg, rgba(30, 200, 255, 0.05) 0%, rgba(30, 200, 255, 0.02) 100%);
            border: 1px solid rgba(30, 200, 255, 0.2);
            border-radius: 12px;
            padding: 40px 30px;
            text-align: center;
            color: #fff;
            transition: all 0.3s ease;
            height: 100%;
          }
          .differentiator-card:hover {
            border-color: rgba(30, 200, 255, 0.5);
            box-shadow: 0 8px 20px rgba(30, 200, 255, 0.1);
            transform: translateY(-5px);
          }
          .card-icon {
            font-size: 48px;
            color: #1ec8ff;
            margin-bottom: 20px;
          }
          .card-title {
            font-size: 1.4rem;
            font-weight: 600;
            margin-bottom: 20px;
            color: #fff;
          }
          .card-list {
            list-style: none;
            padding: 0;
            margin: 0;
            text-align: left;
          }
          .card-list li {
            padding: 10px 0;
            color: #aaa;
            border-bottom: 1px solid rgba(255, 255, 255, 0.05);
            font-size: 0.95rem;
          }
          .card-list li:last-child {
            border-bottom: none;
          }

          /* Hero Styles */
          .page-hero {
            min-height: 100vh;
            position: relative;
            overflow: hidden;
            display: flex;
            align-items: center;
          }
          
         .overlay-video-bg {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          overflow: hidden;
          z-index: 1;
        }

          .overlay-video-bg video {
            width: 100%;
            height: 100%;
            object-fit: cover;
            opacity: 20;
            z-index: 6666;
          }

          
          .page-hero::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: linear-gradient(135deg, rgba(10, 15, 43, 0.1) 0%, rgba(26, 31, 58, 0) 100%);
            z-index: 2;
          }
          
          .page-hero .container {
            position: relative;
            z-index: 3;
          } 

          .hero-title {
            font-size: 3.8rem;
            font-weight: 900;
            color: #fff;
            margin-bottom: 20px;
            line-height: 1.2;
          }
          .featured-text {
            color: #1ec8ff;
          }
          .hero-subtitle {
           font-weight: 900;
            font-size: 1.8rem;
            color: #ccc;
            line-height: 1.8;
            margin-bottom: 30px;
            margin-left: 20px;
          }
          /* Responsive */
          @media (max-width: 768px) {
            .hero-title {
              font-size: 2rem;
            }
            .stat-value {
              font-size: 2rem;
            }
            .sec-heading .title {
              font-size: 1.8rem;
            }
          }

          /* =====================================================
            HOME – CLIENT / TRUSTED BY SECTION (MOBILE FIX)
            ===================================================== */

          @media (max-width: 767px) {

            /* Logo wrapper alignment */
            .trusted-by-section img,
            .clients-section img,
            .logo-wall img {
              width: 100% !important;
              max-width: 120px !important;
              height: 60px !important;

              object-fit: contain !important;
              display: block !important;
              margin: 0 auto !important;
            }

            /* Ensure equal visual spacing */
            .trusted-by-section .col,
            .clients-section .col,
            .logo-wall .col {
              display: flex;
              align-items: center;
              justify-content: center;
              margin-bottom: 24px;
            }
          }

          /* =====================================================
            TABLET – SLIGHTLY LARGER BUT CONSISTENT
            ===================================================== */
          @media (min-width: 768px) and (max-width: 991px) {

            .trusted-by-section img,
            .clients-section img,
            .logo-wall img {
              max-width: 140px !important;
              height: 70px !important;
              object-fit: contain !important;
            }
          }

          /* =====================================================
            DESKTOP – LEAVE AS IS (SAFETY LOCK)
            ===================================================== */
          @media (min-width: 992px) {

            .trusted-by-section img,
            .clients-section img,
            .logo-wall img {
              height: auto;
              max-width: unset;
            }
          }

          /* =====================================================
          MOBILE GAP FIX – CLIENTS → DIFFERENTIATOR
          (SAFE, ISOLATED, NON-DESTRUCTIVE)
          ===================================================== */

        @media (max-width: 767px) {

          /* Reduce excessive section padding */
          .clients-section {
            padding-top: 48px !important;
            padding-bottom: 48px !important;
          }

          /* Reduce space above marquee */
          .clients-marquee {
            margin-top: 32px !important;
          }

          /* Tighten logo containers vertically */
          .client-logo {
            height: 80px !important;
            min-width: 120px !important;
          }

          /* Prevent invisible height from animation track */
          .clients-track {
            align-items: center;
          }

          /* Pull next section closer */
          .differentiator {
            padding-top: 48px !important;
          }
        }


        `}
      </style>
    </>
  );
}
